
import { ArrowDown, Star, Users, MapPin } from "lucide-react";
import { useState } from "react";
import LazyImage from "./LazyImage";

const Hero = () => {
  const [imageLoaded, setImageLoaded] = useState(false);

  return (
    <section id="home" className="relative min-h-screen flex items-center justify-center overflow-hidden">
      {/* Background gradient with Zimbabwe colors */}
      <div className="absolute inset-0 bg-gradient-to-br from-amber-900 via-red-900 to-green-900 opacity-95 z-10"></div>
      
      {/* Optimized background image with proper loading and CLS prevention */}
      <div className="absolute inset-0 z-0" style={{ minHeight: '100vh' }}>
        {!imageLoaded && (
          <div className="absolute inset-0 bg-gradient-to-br from-gray-800 to-gray-900 loading-skeleton"></div>
        )}
        <LazyImage
          src="https://images.unsplash.com/photo-1472396961693-142e6e269027?ixlib=rb-4.0.3&auto=format&fit=crop&w=1920&q=75&fm=webp"
          alt="Zimbabwe landscape showing rural building projects for diaspora tracking"
          className={`w-full h-full object-cover transition-opacity duration-500 ${imageLoaded ? 'opacity-100' : 'opacity-0'}`}
          width={1920}
          height={1080}
          onLoad={() => setImageLoaded(true)}
          placeholder="data:image/jpeg;base64,/9j/4AAQSkZJRgABAQAAAQABAAD/2wBDAAYEBQYFBAYGBQYHBwYIChAKCgkJChQODwwQFxQYGBcUFhYaHSUfGhsjHBYWICwgIyYnKSopGR8tMC0oMCUoKSj/2wBDAQcHBwoIChMKChMoGhYaKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCj/wAARCAAIAAoDASIAAhEBAxEB/8QAFQABAQAAAAAAAAAAAAAAAAAAAAv/xAAhEAACAQMDBQAAAAAAAAAAAAABAgMABAUGIWGRkqGx0f/EABUBAQEAAAAAAAAAAAAAAAAAAAMF/8QAGhEAAgIDAAAAAAAAAAAAAAAAAAECEgMRkf/aAAwDAQACEQMRAD8AltJagyeH0AthI5xdrLcNM91BF5pX2HaH9bcfaSXWGaRmknyJckliyjqTzSlT54b6bk+h0R//2Q=="
        />
      </div>

      {/* Reduced floating elements for better performance */}
      <div className="absolute top-20 left-4 sm:left-10 w-16 h-16 sm:w-20 sm:h-20 bg-yellow-400/20 rounded-full blur-xl animate-pulse z-10"></div>
      <div className="absolute bottom-32 right-8 sm:right-16 w-24 h-24 sm:w-32 sm:h-32 bg-red-600/20 rounded-full blur-xl animate-pulse z-10" style={{animationDelay: '1s'}}></div>

      <div className="relative z-20 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
        {/* Trust indicators - mobile-first design */}
        <div className="flex justify-center items-center gap-2 sm:gap-4 lg:gap-6 mb-6 sm:mb-8 text-white/90 flex-wrap">
          <div className="flex items-center gap-1 sm:gap-2 text-xs sm:text-sm font-medium bg-black/20 backdrop-blur-sm px-2 sm:px-3 py-1 sm:py-2 rounded-full">
            <span className="text-sm sm:text-lg" role="img" aria-label="Zimbabwe flag">🇿🇼</span>
            <Star className="w-3 h-3 sm:w-4 sm:h-4 fill-yellow-400 text-yellow-400" aria-hidden="true" />
            <span className="hidden sm:inline">5,000+ Verified Prices</span>
            <span className="sm:hidden">5K+ Verified</span>
          </div>
          <div className="hidden md:block w-px h-4 bg-white/30" aria-hidden="true"></div>
          <div className="flex items-center gap-1 sm:gap-2 text-xs sm:text-sm font-medium bg-black/20 backdrop-blur-sm px-2 sm:px-3 py-1 sm:py-2 rounded-full">
            <Users className="w-3 h-3 sm:w-4 sm:h-4" aria-hidden="true" />
            <span className="hidden sm:inline">2,000+ Building Projects</span>
            <span className="sm:hidden">2K+ Projects</span>
          </div>
          <div className="hidden md:block w-px h-4 bg-white/30" aria-hidden="true"></div>
          <div className="flex items-center gap-1 sm:gap-2 text-xs sm:text-sm font-medium bg-black/20 backdrop-blur-sm px-2 sm:px-3 py-1 sm:py-2 rounded-full">
            <MapPin className="w-3 h-3 sm:w-4 sm:h-4" aria-hidden="true" />
            <span className="hidden sm:inline">Used in 10+ Countries</span>
            <span className="sm:hidden">10+ Countries</span>
          </div>
        </div>

        <div className="animate-fade-in">
          <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl xl:text-7xl font-bold text-white mb-4 sm:mb-6 leading-tight px-2">
            Track Building Projects in 
            <span className="block bg-gradient-to-r from-yellow-400 via-yellow-300 to-yellow-500 bg-clip-text text-transparent mt-2">
              Zimbabwe
            </span>
            <span className="block text-2xl sm:text-3xl md:text-4xl lg:text-5xl xl:text-6xl text-amber-100 mt-2">
              from Anywhere
            </span>
          </h1>
          
          <p className="text-lg sm:text-xl md:text-2xl text-amber-100 mb-8 sm:mb-12 max-w-4xl mx-auto leading-relaxed px-2">
            ZimKonnect helps diaspora Zimbabweans monitor every brick, every payment, 
            every milestone — from the comfort of your phone.
          </p>

          <div className="flex flex-col sm:flex-row gap-4 sm:gap-6 justify-center items-center mb-12 sm:mb-16 px-4 max-w-2xl mx-auto">
            <button 
              onClick={() => document.getElementById('join-waitlist')?.scrollIntoView({ behavior: 'smooth' })}
              className="group w-full sm:w-auto bg-gradient-to-r from-yellow-500 to-yellow-600 text-black px-8 sm:px-10 py-4 sm:py-5 rounded-2xl text-lg sm:text-xl font-bold hover:from-yellow-400 hover:to-yellow-500 transition-all duration-300 transform hover:scale-105 shadow-2xl hover:shadow-3xl min-h-[56px] sm:min-h-[64px] touch-manipulation relative overflow-hidden"
              aria-label="Get early access to Zimbabwe project tracking platform for diaspora communities"
            >
              <span className="relative z-10">Get Early Access</span>
              <div className="absolute inset-0 bg-gradient-to-r from-yellow-400 to-yellow-500 opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
            </button>
            <button 
              onClick={() => document.getElementById('how-it-works')?.scrollIntoView({ behavior: 'smooth' })}
              className="group w-full sm:w-auto bg-white/10 backdrop-blur-md text-white px-8 sm:px-10 py-4 sm:py-5 rounded-2xl text-lg sm:text-xl font-semibold border-2 border-white/30 hover:bg-white/20 hover:border-white/50 transition-all duration-300 transform hover:scale-105 shadow-lg hover:shadow-xl min-h-[56px] sm:min-h-[64px] touch-manipulation"
              aria-label="Learn how Zimbabwe project tracking works for diaspora families"
            >
              <span>Learn How It Works</span>
            </button>
          </div>

          {/* Mission statement - mobile-optimized */}
          <div className="flex flex-col items-center gap-3 sm:gap-4 px-4">
            <div className="flex items-center gap-2 sm:gap-3 bg-green-700/30 backdrop-blur-sm px-4 sm:px-6 lg:px-8 py-2 sm:py-3 lg:py-4 rounded-full border border-green-500/40">
              <div className="w-2 h-2 sm:w-3 sm:h-3 bg-green-400 rounded-full animate-pulse" aria-hidden="true"></div>
              <span className="text-green-100 font-semibold text-sm sm:text-base lg:text-lg">Building Trust from Afar</span>
            </div>
            <div className="text-amber-200 text-sm sm:text-base lg:text-lg font-medium text-center max-w-md sm:max-w-lg">
              Trusted by diaspora families across 🇬🇧 🇺🇸 🇦🇺 🇨🇦 and more
            </div>
          </div>
        </div>

        {/* Scroll indicator - mobile-friendly */}
        <div className="absolute bottom-6 sm:bottom-8 left-1/2 transform -translate-x-1/2 animate-bounce">
          <div className="w-5 h-8 sm:w-6 sm:h-10 border-2 border-yellow-400/60 rounded-full flex justify-center" aria-hidden="true">
            <div className="w-1 h-2 sm:h-3 bg-yellow-400/80 rounded-full mt-1 sm:mt-2 animate-pulse"></div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Hero;
